import React, { useMemo } from 'react';
import { useIsFetching, useMutation, useQueryClient } from '@tanstack/react-query';
import Builder from './Builder';
import useOption from '../../../hooks/useOption';
import useBackgrounds from '../../../hooks/useBackgrounds';
import useImageTypes from '../../../hooks/useImageTypes';
import usePoses from '../../../hooks/usePoses';
import Sheet from '../../../models/Sheet';
import { ImageType } from '../../../models';
import QUERY_KEYS from '../../../utils/QueryKeys';
import usePackagePoses from '../../../hooks/usePackagePoses';

export default function SeniorMultiPose({
  cartId,
  optionId,
  packageId,
  schoolId,
  studentId,
  orderPackage,
  onSave,
  favoriteIds,
}) {
  // API Data
  const { data: option } = useOption(optionId, schoolId);
  const { data: backgrounds } = useBackgrounds(packageId);
  const { data: imageTypes } = useImageTypes(option && option.id);
  const { data: poses } = usePoses(studentId, packageId);
  const { data: packagePoses } = usePackagePoses(packageId);

  const queryClient = useQueryClient();

  // The yearbook pose is now chosen in the dedicated step 2 (flagged is_yearbook_pose)
  // and its $0 line is already in the cart. So the package build no longer asks the
  // senior to pick a yearbook pose a second time: when a yearbook pose is on record we
  // suppress the appended "Yearbook Pose" slot and set the package OrderPackage's
  // seniorImageId from that pose. Defensive fallback: if (somehow) no yearbook pose is
  // flagged, keep the old behavior of appending the slot so nothing breaks.
  const yearbookPose = useMemo(
    () => (poses || []).find((pose) => pose.isYearbookPose === true),
    [poses],
  );

  const imageTypesWithYearbook = useMemo(() => {
    if (yearbookPose) return imageTypes;

    const yearbookImageType = new ImageType({
      name: 'Yearbook Pose',
      count: 1,
      id: -1,
    });

    return [...imageTypes, yearbookImageType];
  }, [imageTypes.length, yearbookPose]);

  const {
    mutate,
    isLoading,
  } = useMutation({
    mutationFn: ({ sheets, additionalPoses }) => {
      // Destroy any sheets on the orderPackage (if any), and replace with the new sheets
      const oldSheets = orderPackage.sheets.map((sheet) => {
        sheet.isMarkedForDestruction = true;

        return sheet;
      });

      const yearbookSheet = sheets.find((sheet) => sheet.imageType.name === 'Yearbook Pose');
      const newSheets = sheets.filter((sheet) => sheet.imageType.name !== 'Yearbook Pose').map((sheetParams) => new Sheet({ ...sheetParams }));

      orderPackage.cartId = cartId;
      orderPackage.studentId = studentId;
      orderPackage.optionId = option.id;
      orderPackage.packageId = packageId;
      orderPackage.apiOption = option;
      orderPackage.extraPoses = additionalPoses;
      orderPackage.sheets = [...oldSheets, ...newSheets];
      // Prefer the step-2 yearbook pose; fall back to the (legacy) appended slot.
      orderPackage.seniorImageId = yearbookPose?.id || yearbookSheet?.poseId;

      return orderPackage.save({ with: 'sheets' });
    },
    onSuccess: () => {
      if (Object.keys(orderPackage.errors).length > 0) return;

      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.cart, QUERY_KEYS.numItems] });

      onSave();
    },
  });

  // UI State
  const isFetching = useIsFetching();

  if (isFetching) return null;
  if (!option) return null;

  return (
    <Builder
      packagePoses={packagePoses}
      backgrounds={backgrounds}
      favoriteIds={favoriteIds}
      isLoading={isLoading}
      initialSheets={orderPackage.sheets}
      errors={orderPackage.errors}
      imageTypes={imageTypesWithYearbook}
      packageId={packageId}
      poses={poses}
      onConfirm={(sheets, additionalPoses) => mutate({ sheets, additionalPoses })}
      option={option}
    />
  );
}
